import Vue from 'vue';
import Component from 'vue-class-component';
import Router from 'vue-router';
import { routes } from '@/router/routes.js';
import { IS_FRANCE_CLIENT, REDIRECT_COUNTRY_CODE, REDIRECT_URL, TLSCITIZEN_WEBAPP_URL } from '@/constants';
import { getRedirectionListCache, ROUTE_MIGRATION_MAP } from '@/shared/utils/utils';

Component.registerHooks([
  'beforeRouteEnter',
  'beforeRouteLeave',
  'beforeRouteUpdate' // for vue-router 2.2+
]);
const Error = () => import('../core/error/error.vue');
const LoginMaximum = () => import('../core/error/login-maximum.vue');
const JhiConfigurationComponent = () => import('../admin/configuration/configuration.vue');
const JhiDocsComponent = () => import('../admin/docs/docs.vue');
const JhiHealthComponent = () => import('../admin/health/health.vue');
const JhiLogsComponent = () => import('../admin/logs/logs.vue');
const JhiMetricsComponent = () => import('../admin/metrics/metrics.vue');
const JhiGatewayComponent = () => import('../admin/gateway/gateway.vue');
/* tslint:disable */
// jhipster-needle-add-entity-to-router-import - JHipster will import entities to the router here

// prettier-ignore
const Form = () => import('../visa-application/form/form.vue');
const Personal = () => import('../visa-application/personal/personal.vue');
const Instruction = () => import('../visa-application/Instruction/Instruction.vue');
const FormSubmission = () => import('../visa-application/form/form-submission.vue');
const FormUpdate = () => import('../visa-application/form/form-update.vue');
const FormGroup = () => import('../visa-application/form/form-group.vue');
const INDIFormGroup = () => import('../visa-application/form/indi-form-group.vue');
const Welcome = () => import('../visa-application/form/welcome.vue');
const Appointment = () => import('../visa-application/appointment/appointment.vue');
const AppointmentProposal = () => import('../visa-application/appointment/appointment-proposal.vue');
const AppointmentConfirmation = () => import('../visa-application/appointment/appointment-confirmation.vue');
const Checkout = () => import('../visa-application/checkout/checkout.vue');
const CheckoutConfirm = () => import('../visa-application/checkout/checkout-confirm.vue');
const Unsubscribe = () => import('../visa-application/unsubscribe/unsubscribe.vue');
const PassbackAddress = () => import('../visa-application/checkout/passback-address.vue');
const ServicesProcess = () => import('../info/services/services-process.vue');
const ServicesDocument = () => import('../info/services/services-document.vue');
const ServicesFee = () => import('../info/services/services-fee.vue');
const Insurte = () => import('../info/insurte/insurte.vue');
const CustomizedCountry = () => import('../info/custom-page/customized-country.vue');
const CustomizedCenter = () => import('../info/custom-page/customized-center.vue');
const CustomizedGlobal = () => import('../info/custom-page/customized-global.vue');
const OTPVerification = () => import('../visa-application/OTPVerification/OTPVerification.vue');
const ApplierResidenceCountry = () => import('../visa-application/applier-residence-country/applier-residence-country.vue');
const FormSequence = () => import('../visa-application/form/form-sequence.vue');
const isVisaCutService = () => {
  return process.env.IS_SERVICE === 'true';
};
Vue.use(Router);

const scrollBehavior = to => {
  if (to.hash) {
    return { selector: to.hash };
  } else {
    return { x: 0, y: 0 };
  }
};

const redirectByRedirectCountryCode = residenceCountryCode => {
  return (
    REDIRECT_COUNTRY_CODE.split(',').includes(residenceCountryCode) &&
    REDIRECT_URL !== undefined &&
    !window.location.href.includes(REDIRECT_URL)
  ); //do nothing when on WAF
};

// prettier-ignore
const router = new Router({
  mode: 'history',
  routes: [
    ...routes,
    // --- Technical routes --- //
    {
      path: '/forbidden',
      name: 'Forbidden',
      component: Error,
      meta: {
        error403: true,
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/not-found',
      name: 'NotFound',
      component: Error,
      meta: {
        error404: true,
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    // --- Admin/JHipster routes --- //
    {
      path: '/admin/docs',
      name: 'JhiDocsComponent',
      component: JhiDocsComponent,
      meta: {
        authorities: ['ROLE_ADMIN', 'ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/admin/jhi-health',
      name: 'JhiHealthComponent',
      component: JhiHealthComponent,
      meta: {
        authorities: ['ROLE_ADMIN'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/admin/logs',
      name: 'JhiLogsComponent',
      component: JhiLogsComponent,
      meta: {
        authorities: ['ROLE_ADMIN'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/admin/jhi-metrics',
      name: 'JhiMetricsComponent',
      component: JhiMetricsComponent,
      meta: {
        authorities: ['ROLE_ADMIN'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/admin/jhi-configuration',
      name: 'JhiConfigurationComponent',
      component: JhiConfigurationComponent,
      meta: {
        authorities: ['ROLE_ADMIN'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/admin/gateway',
      name: 'JhiGatewayComponent',
      component: JhiGatewayComponent,
      meta: {
        authorities: ['ROLE_ADMIN'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    // --- User routes --- //
    {
      path: '/forms/:residenceCountryCode/:issuer/:formGroupId',
      name: 'Form',
      component: Form,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/forms/' + to.params.residenceCountryCode + '/' + to.params.issuer + '/' + to.params.formGroupId;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/personal/:residenceCountryCode/:issuer/:formGroupId',
      name: 'Personal',
      component: Personal,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/personal/' + to.params.residenceCountryCode + '/' + to.params.issuer + '/' + to.params.formGroupId;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/instruction/:residenceCountryCode/:issuer/:formGroupId',
      name: 'Instruction',
      component: Instruction,
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/formSubmission/:residenceCountryCode/:issuer/:formGroupId',
      name: 'FormSubmission',
      component: FormSubmission,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/formSubmission/' + to.params.residenceCountryCode + '/' + to.params.issuer + '/' + to.params.formGroupId;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/formGroup/:residenceCountryCode/:issuer',
      name: 'FormGroup',
      component: FormGroup,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/formGroup/' + to.params.residenceCountryCode + '/' + to.params.issuer;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/form-sequence/:residenceCountryCode/:issuer/:formGroupId',
      name: 'FormSequence',
      component: FormSequence,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/INDIFormGroup/:residenceCountryCode/:issuer',
      name: 'INDIFormGroup',
      component: INDIFormGroup,
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/welcome/:residenceCountryCode/:issuer',
      name: 'Welcome',
      component: Welcome,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/welcome/' + to.params.residenceCountryCode + '/' + to.params.issuer;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/form/:residenceCountryCode/:issuer/:formGroupId/:formId/duplicateInto/:newFormId',
      name: 'FormDuplicate',
      component: FormUpdate,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/form/' + to.params.residenceCountryCode + '/' + to.params.issuer + '/' + to.params.formGroupId + '/' + to.params.formId + '/duplicateInto/' + to.params.newFormId;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/form/:residenceCountryCode/:issuer/edit/:formGroupId/:formId',
      name: 'FormEdit',
      component: FormUpdate,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/form/' + to.params.residenceCountryCode + '/' + to.params.issuer + '/edit/' + to.params.formGroupId + '/' + to.params.formId;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/appointment/confirmation/:residenceCountryCode/:issuer',
      name: 'AppointmentConfirmation',
      component: AppointmentConfirmation,
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/appointment/:residenceCountryCode/:issuer/:formGroupId',
      name: 'Appointment',
      component: Appointment,
      beforeEnter(to, from, next) {
        if (redirectByRedirectCountryCode(to.params.residenceCountryCode)) {
          window.location.href =
            'https://' + REDIRECT_URL + '/appointment/' + to.params.residenceCountryCode + '/' + to.params.issuer + '/' + to.params.formGroupId;
        } else next();
      },
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/appointment-proposal/:residenceCountryCode/:issuer/:formGroupId',
      name: 'AppointmentProposal',
      component: AppointmentProposal,
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/checkout/confirm/:residenceCountryCode/:issuer/:formGroupId',
      name: 'CheckoutConfirm',
      component: CheckoutConfirm,
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/passback-address/:residenceCountryCode/:issuer/:formGroupId',
      name: 'PassbackAddress',
      component: PassbackAddress,
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/checkout/:residenceCountryCode/:issuer/:formGroupId',
      name: 'Checkout',
      component: Checkout,
      meta: {
        authorities: ['ROLE_USER'],
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/unsubscribe/:residenceCountryCode/:issuer',
      name: 'Unsubscribe',
      component: Unsubscribe,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/services/:serviceName/procedure/:residenceCountryCode/:issuer',
      name: 'ServicesProcess',
      component: ServicesProcess,
      meta: {
        metaInfo: {
          meta_title: 'TLScontact'
        }
      }
    },
    {
      path: '/services/:serviceName/documents/:residenceCountryCode/:issuer',
      name: 'ServicesDocument',
      component: ServicesDocument,
      meta: {
        metaInfo: {
          meta_title: 'TLScontact'
        }
      }
    },
    {
      path: '/services/:serviceName/fees/:residenceCountryCode/:issuer',
      name: 'ServicesFee',
      component: ServicesFee,
      meta: {
        metaInfo: {
          meta_title: 'TLScontact'
        }
      }
    },
    {
      path: '/insurte/:residenceCountryCode/:issuer',
      name: 'Insurte',
      component: Insurte,
      meta: {
        metaInfo: {
          meta_title: 'TLScontact'
        }
      }
    },
    {
      path: '/visa/:issuer/:page',
      name: 'customizedGlobal',
      component: CustomizedGlobal,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      },
      beforeEnter(to, from, next) {
        // 如果是 hmpo环境 或者是legalization 环境就切换显示路径
        // If the environment is HMPO or Legalization, switch the display path
        if (isVisaCutService()) {
          next({
            name: 'customizedGlobalService',
            params: {
              issuer: to.params.issuer,
              page: to.params.page,
            }
          })
        }else next();
      }
    },
    {
      path: '/service/:issuer/:page',
      name: 'customizedGlobalService',
      component: CustomizedGlobal,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/OTPVerification/:residenceCountryCode/:issuer/:formGroupId',
      name: 'OTPVerification',
      component: OTPVerification,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/:page/:residenceCountryCode/:issuer',
      name: 'customizedCountry',
      component: CustomizedCountry,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/:page/:issuer',
      name: 'customizedCenter',
      component: CustomizedCenter,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '/login-maximum',
      name: 'LoginMaximum',
      component: LoginMaximum,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
    {
      path: '*',
      name: 'catchAll',
      component: ApplierResidenceCountry,
      meta: {
        metaInfo: {
          meta_title: "Customer Gateway"
        }
      }
    },
  ],
  scrollBehavior,
});

export default router;

router.beforeEach(handleMigratedIssuers);

async function handleMigratedIssuers(to, from, next) {
  if (!IS_FRANCE_CLIENT || !TLSCITIZEN_WEBAPP_URL) {
    next();
    return;
  }

  const vacId = to.params.issuer || '';
  const countryId = to.params.residenceCountryCode || vacId.slice(0, 2) || '';

  const v1Countries = await getRedirectionListCache();

  if (!v1Countries || v1Countries.includes(countryId)) {
    next();
    return;
  }

  const resolvedPaths = Object.entries(ROUTE_MIGRATION_MAP).map(([v1Path, v2Path]) => [
    v1Path.replace(':vacId', vacId).replace(':countryId', countryId),
    v2Path.replace(':vacId', vacId).replace(':countryId', countryId)
  ]);

  const path = resolvedPaths.find(([v1Path]) => v1Path === to.path);
  const v2Path = path ? path[1] : '';

  if (v2Path !== '') {
    location.href = `${TLSCITIZEN_WEBAPP_URL}${v2Path}`;
  }

  next();
}
